import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { MealPlanView } from '@web-app/components/CreateMealPlan/utils';
import { useMolecule } from 'bunshi/react';
import { useAtomValue } from 'jotai';

export type MealPlanCardStatus =
  | 'idle'
  | 'generating-instructions'
  | 'reviewing-summary'
  | 'generating-summary'
  | 'ready'
  | 'sending'
  | 'sent';

const STATUS_LABELS: Record<MealPlanCardStatus, string> = {
  idle: 'Start building your meal plan',
  'generating-instructions': 'Generating recipe outline...',
  'reviewing-summary': 'Your recipe outline is ready!',
  'generating-summary': 'Generating meal plan recipes...',
  ready: 'Your meal plan recipes are ready!',
  sending: 'Sending meal plan...',
  sent: 'Your meal plan was sent!',
};

export function useMealPlanCardStatus(): {
  status: MealPlanCardStatus;
  label: string;
} {
  const {
    activeMealPlanStepAtom,
    hasSetInitialStepAtom,
    isStandalonePageOpenAtom,
    isMealPlanBusyAtom,
    isMealPlanSentAtom,
    detailedMealPlanAtom,
    mealPlanInstructionsAtom,
  } = useMolecule(mealPlanMolecule);

  const activeStep = useAtomValue(activeMealPlanStepAtom);
  const hasSetInitialStep = useAtomValue(hasSetInitialStepAtom);
  const isStandalonePageOpen = useAtomValue(isStandalonePageOpenAtom);
  const isBusy = useAtomValue(isMealPlanBusyAtom);
  const isSent = useAtomValue(isMealPlanSentAtom);
  const detailedMealPlan = useAtomValue(detailedMealPlanAtom);
  const instructions = useAtomValue(mealPlanInstructionsAtom);

  // Step-based status: use the broadcast activeMealPlanStepAtom + isBusy to
  // show granular progress (e.g. "Reviewing summary", "Generating meal plan...").
  //
  // Activated when ANY of these are true:
  //   - hasSetInitialStep: wizard was opened in this tab (set by CreateMealPlan)
  //   - isStandalonePageOpen: wizard is open in another tab right now
  //   - activeStep advanced past Instructions: step was set via broadcast and
  //     persists after the standalone tab closes (atom retains last value)
  //
  // Data-based fallback: before the wizard has ever been opened, uses query-
  // backed data presence (detailedMealPlan, instructions) for coarser status.
  //
  // isSent always wins regardless of strategy — covers both broadcast and query.
  const stepHasAdvanced = activeStep !== MealPlanView.Instructions;
  const useStepBasedStatus =
    hasSetInitialStep || isStandalonePageOpen || stepHasAdvanced;

  let status: MealPlanCardStatus = 'idle';

  if (isSent || detailedMealPlan?.isSent) {
    status = 'sent';
  } else if (useStepBasedStatus) {
    if (activeStep === MealPlanView.Details && isBusy) {
      status = 'sending';
    } else if (activeStep === MealPlanView.Details) {
      status = 'ready';
    } else if (activeStep === MealPlanView.Summary && !isBusy) {
      status = 'reviewing-summary';
    } else if (activeStep === MealPlanView.Summary && isBusy) {
      status = 'generating-summary';
    } else if (activeStep === MealPlanView.Instructions && isBusy) {
      status = 'generating-instructions';
    }
  } else {
    // Wizard hasn't been opened and no step broadcast received — use data presence
    if (detailedMealPlan) {
      status = 'ready';
    } else if (isBusy) {
      status = 'generating-instructions';
    } else if (instructions) {
      status = 'idle';
    }
  }

  return { status, label: STATUS_LABELS[status] };
}
